/**
 * Represents data for selecting a period type, either Actual or Budget.
 * `iconNameAndStylingLeft` should follow the format `iconName; style`.
 * Possible styles include 'main-8' or 'orange-11'.
 */
export interface MetricDataRoleForSelector {
  name: string;
  iconNameAndStylingLeft: string;
}

/**
 * Represents data for managing periods within a specific year in the modal-manage-period.component table.
 * Includes the year number, selection status of months, and details about individual months.
 * - `selected`: Indicates the overall selection status of months in this year:
 *    - 'all': All months are selected.
 *    - 'some': Some months are selected.
 *    - 'none': No months are selected.
 * - `allSelectedDisabled`: Indicates if all months are already present in the database,
 *    which disables creation for this year.
 * - `months`: An array of MetricPeriodMonth representing individual months within the year.
 */
export class MetricYearPlanning {
  year: number;
  selected: MetricYearSelectionStatus = 'none';

  /**
   * If true, indicates that all months within this year are already present in the database,
   * disabling the creation of new periods for this year.
   */
  allSelectedDisabled = false;

  months: MetricPeriodMonth[];

  constructor(year: number) {
    this.year = year;
    this.months = this.initializeMonths();
  }

  private initializeMonths(): MetricPeriodMonth[] {
    return MONTH_NAMES.map((name) => new MetricPeriodMonth(name, false));
  }
}

/**
 * Represents a month within a MetricYearPlanning, providing information about its name, selection status,
 * and whether it is disabled.
 */

export class MetricPeriodMonth {
  // The name of the month (e.g., 'Jan', 'Feb', etc.).
  name: string;
  selected: boolean;

  // If `true`, indicates that this month within its year already exists in the database.
  disabled: boolean;

  constructor(name: string, selected: boolean = true) {
    this.name = name;
    this.selected = selected;
    this.disabled = false;
  }

  /**
   * Returns the numerical index of the month within a standard calendar year (0 for January, 11 for December).
   *
   * @returns The numerical index of the month.
   */
  getNumber(): number {
    return MONTH_NAMES.indexOf(this.name);
  }

  /**
   * Provides the full month with leading '0' if needed. E.x.
   * Jan: 01
   * Dec: 12
   *
   * @return the string index of the month.
   */
  getFullMonth(): string {
    const index = this.getNumber() + 1;
    return (index < 10 ? '0' : '') + index;
  }
}

export class MetricPeriodYearAndMonth {
  year: number;
  month: number;
}

export interface MetricYearsForAddPeriodSelector {
  all: YearDisabled[];
  selected: YearDisabled;
}

export interface YearDisabled {
  disabled: boolean;
  year: number;
}

export type MetricYearSelectionStatus = 'all' | 'some' | 'none';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
